import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const Services = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const [currentIndex, setCurrentIndex] = useState(0);

  const services = [
    {
      id: 1,
      title: "Injectables",
      description: "Botox, dermal fillers, and PRP treatments to smooth wrinkles, restore volume, and rejuvenate your appearance.",
      image: "b.png",
      route: "/injectables"
    },
    {
      id: 2,
      title: "Facials",
      description: "HydraFacial and Moveo Glo Laser Facial treatments for deep cleansing, hydration, and radiant skin.",
      image: "hydra.png",
      route: "/facials"
    },
    {
      id: 3,
      title: "Body Treatments",
      description: "Microneedling, dermaplaning, and Morpheus 8 RF therapy for improved skin texture and rejuvenation.",
      image: "/morpheus.jpg",
      route: "/body-treatments"
    },
   
    {
      id: 5,
      title: "IV Therapy",
      description: "Vitamin and nutrient infusions delivered directly into your bloodstream for optimal health and wellness.",
      image: "/ivtherapy.webp",
      route: "/iv-therapy"
    },
    {
      id: 6,
      title: "Men's Health",
      description: "Testosterone replacement therapy and Apex RF treatment for erectile dysfunction and male wellness.",
      image: "/mensconsult.jpg",
      route: "/mens-health"
    },
    {
      id: 7,
      title: "Peptides",
      description: "NAD+, Sermorelin, BPC 157, and other peptide injections for cellular energy, healing, and anti-aging.",
      image: "/pep.webp",
      route: "/peptides"
    },
    {
      id: 8,
      title: "Muscle Sculpting",
      description: "Tone electromagnetic technology to build muscle mass and burn fat non-invasively.",
      image: "muscleSculpt.jpg",
      route: "/muscle-sculpting"
    },
    {
      id: 9,
      title: "Massage",
      description: "Swedish, deep tissue massage, and cupping therapy for relaxation, pain relief, and muscle recovery.",
      image: "/massage.webp",
      route: "/massage"
    },
    {
      id: 10,
      title: "Laser Hair Removal",
      description: "Achieve smooth, hair-free skin with our painless Motus AZ+ laser hair removal for all skin types.",
      image: "/laserhair.png",
      route: "/laser-hair-removal"
    },
    {
      id: 11,
      title: "Waxing",
      description: "Professional waxing services for smooth, long-lasting hair removal from face to body.",
      image: "/wax.jpeg",
      route: "/waxing"
    },
    {
      id: 12,
      title: "Medical Weight Loss",
      description: "Personalized weight loss programs with Semaglutide, Tirzepatide, and Retatrutide treatments.",
      image: "med.jpg",
      route: "/medical-weight"
    },
    {
      id: 13,
      title: "Pigmented Lesion",
      description: "Laser treatment to reduce age spots, sun damage, and hyperpigmentation for even skin tone.",
      image: "/pigment.png",
      route: "/pigmented-lesion"
    },
    {
      id: 14,
      title: "Vascular Lesion",
      description: "Advanced laser treatment to reduce spider veins, broken capillaries, and rosacea.",
      image: "vascu.png",
      route: "/vascular-lesion"
    },
    {
      id: 15,
      title: "Skin Tags",
      description: "Quick, safe removal of benign skin tags for smooth, clear skin at $25 per tag.",
      image: "/tag.png",
      route: "/skin-tag"
    }
  ];

  // Responsive services per page
  const getServicesPerPage = () => {
    if (typeof window === 'undefined') return 3;
    if (window.innerWidth < 768) return 1; // Mobile: 1 per page
    if (window.innerWidth < 1024) return 2; // Tablet: 2 per page
    return 3; // Desktop: 3 per page
  };

  const [servicesPerPage, setServicesPerPage] = useState(getServicesPerPage());

  // Update servicesPerPage on resize
  useEffect(() => {
    const handleResize = () => {
      setServicesPerPage(getServicesPerPage());
      // Reset to first page if current page would be empty after resize
      const newTotalPages = Math.ceil(services.length / getServicesPerPage());
      if (currentIndex >= newTotalPages) {
        setCurrentIndex(0);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [currentIndex, services.length]);

  const totalPages = Math.ceil(services.length / servicesPerPage);

  const nextSlide = () => {
    setCurrentIndex((prev) => (prev + 1) % totalPages);
  };

  const prevSlide = () => {
    setCurrentIndex((prev) => (prev - 1 + totalPages) % totalPages);
  };

  const getCurrentServices = () => {
    const start = currentIndex * servicesPerPage;
    return services.slice(start, start + servicesPerPage);
  };

  const handleServiceClick = (route, isExternal = false) => {
    if (isExternal) {
      // Open external link in new tab
      window.open(route, '_blank', 'noopener,noreferrer');
    } else {
      // Navigate using window.location for internal routes
      window.location.href = route;
    }
  };

  return (
    <section id="services" ref={ref} className="py-16 md:py-24 bg-olive-darkest" aria-label="Our Medical Spa Services">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-12 md:mb-16"
        >
          <h2 className="font-heading text-4xl sm:text-5xl md:text-6xl font-semibold text-cream mb-3 md:mb-4 tracking-[0.3em] uppercase">
            Our Services
          </h2>
          <div className="w-16 md:w-20 h-0.5 bg-gold mx-auto mb-4 md:mb-6" aria-hidden="true"></div>
          <p className="text-cream/80 text-base sm:text-lg max-w-3xl mx-auto leading-relaxed px-4">
            Discover our comprehensive range of medical and aesthetic services designed to enhance your health and well-being.
          </p>
        </motion.div>

        {/* Carousel Container */}
        <div className="relative" role="region" aria-label="Service carousel">
          {/* Services Grid */}
          <AnimatePresence mode="wait">
            <motion.div
              key={currentIndex}
              initial={{ opacity: 0, x: 100 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -100 }}
              transition={{ duration: 0.5 }}
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-8"
            >
              {getCurrentServices().map((service, index) => (
                <article
                  key={`${currentIndex}-${index}`}
                  onClick={() => handleServiceClick(service.route, service.external)}
                  className="relative overflow-hidden border border-cream/20 cursor-pointer group bg-olive-darkest hover:shadow-2xl transition-shadow duration-300"
                >
                  {/* Image */}
                  <div className="relative h-64 overflow-hidden bg-olive-dark">
                    <img 
                      src={service.image} 
                      alt={`${service.title} - ${service.description}`}
                      className={`w-full h-full transition-transform duration-500 group-hover:scale-110 ${
                        service.id === 3 || service.id === 14 ? 'object-contain' : 'object-cover'
                      }`}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-olive-darkest via-olive-darkest/50 to-transparent opacity-60 group-hover:opacity-80 transition-opacity duration-300" aria-hidden="true"></div>
                  </div>

                  {/* Content */}
                  <div className="p-6">
                    <h3 className="font-heading text-xl sm:text-2xl text-cream mb-3 tracking-[0.15em] uppercase">
                      {service.title}
                    </h3>
                    <p className="text-cream/80 leading-relaxed text-sm sm:text-base mb-4">
                      {service.description}
                    </p>
                    <span 
                      className="inline-flex items-center text-cream text-sm tracking-[0.15em] uppercase border-b-2 border-gold pb-1 transition-all duration-300 group-hover:text-gold group-hover:border-transparent"
                      aria-label={`Learn more about ${service.title}`}
                    >
                      Learn More →
                    </span>
                  </div>
                </article>
              ))}
            </motion.div>
          </AnimatePresence>

          {/* Navigation Buttons */}
          <div className="flex justify-center items-center gap-3 md:gap-4 mt-10 md:mt-12">
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
              onClick={prevSlide}
              className="bg-cream text-olive-darkest p-2.5 md:p-3 rounded-full hover:bg-gold hover:text-olive-darkest transition-all duration-300 shadow-lg active:scale-95 flex-shrink-0"
              aria-label="Previous services"
            >
              <ChevronLeft className="w-5 h-5 md:w-6 md:h-6" />
            </motion.button>

            {/* Dots Indicator */}
            <div className="flex gap-1.5 md:gap-2 overflow-x-auto max-w-[200px] sm:max-w-none">
              {Array.from({ length: totalPages }).map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentIndex(index)}
                  className={`h-2.5 md:h-3 rounded-full transition-all duration-300 flex-shrink-0 ${
                    currentIndex === index 
                      ? 'bg-cream w-6 md:w-8' 
                      : 'bg-cream/30 hover:bg-cream/50 w-2.5 md:w-3'
                  }`}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>

            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
              onClick={nextSlide}
              className="bg-cream text-olive-darkest p-2.5 md:p-3 rounded-full hover:bg-gold hover:text-olive-darkest transition-all duration-300 shadow-lg active:scale-95 flex-shrink-0"
              aria-label="Next services"
            >
              <ChevronRight className="w-5 h-5 md:w-6 md:h-6" />
            </motion.button>
          </div>

          {/* Swipe Hint for Mobile */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: isInView ? 1 : 0 }}
            transition={{ duration: 0.6, delay: 1 }}
            className="text-center text-xs sm:text-sm text-cream/50 mt-4 md:hidden"
          >
            Swipe or use arrows to see more
          </motion.p>
        </div>
      </div>
    </section>
  );
};

export default Services;